import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  MapPin, Search, Globe, CheckCircle2, ArrowRight,
  TrendingUp, Star, Phone, Bot, BarChart2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ServiceQuestionnaireModal from "@/components/ServiceQuestionnaireModal";

const CITY_DATA = {
  "el-paso": { name: "El Paso", region: "West Texas", pop: "680K+", demonym: "El Paso" },
  "lubbock": { name: "Lubbock", region: "West Texas", pop: "260K+", demonym: "Lubbock" },
  "waco": { name: "Waco", region: "Central Texas", pop: "140K+", demonym: "Waco" },
  "killeen": { name: "Killeen", region: "Central Texas", pop: "150K+", demonym: "Killeen" },
  "amarillo": { name: "Amarillo", region: "Texas Panhandle", pop: "200K+", demonym: "Amarillo" },
};

const SERVICE_DATA = {
  "seo": {
    name: "SEO",
    fullName: "SEO Services",
    icon: Search,
    color: "green",
    colorClass: "text-green-400",
    bgClass: "bg-green-500/10 border-green-500/20",
    desc: (city) => `Drive organic traffic and dominate Google search results for ${city}-area businesses. Our SEO strategies combine technical optimization, content authority, and AI search visibility.`,
    benefits: (city) => [
      `Keyword research tailored to ${city} search patterns`,
      "Technical SEO audit and fix implementation",
      "Content strategy & authority building",
      "Monthly ranking reports with ROI tracking",
      "AI search visibility (GEO/AEO) included",
      "Dedicated account manager",
    ],
    faqs: (city) => [
      { q: `How long does SEO take to work in ${city}?`, a: `For ${city} businesses, we typically see measurable improvements in 3-6 months. Local keywords with moderate competition can rank faster (2-3 months), while more competitive terms take 6-12 months. We provide monthly progress reports so you always know where you stand.` },
      { q: `What makes ${city} SEO different from Dallas SEO?`, a: `${city} has its own competitive landscape, search patterns, and local directories. We customize our strategy for ${city}'s market — focusing on local keywords, ${city}-specific directories and citations, and content that resonates with the ${city} audience.` },
      { q: "Do you guarantee first-page rankings?", a: "No ethical SEO agency can guarantee specific rankings — Google's algorithm uses 200+ factors. What we guarantee is a data-driven strategy, transparent reporting, and measurable improvements in organic traffic and lead generation." },
    ],
  },
  "local-seo": {
    name: "Local SEO",
    fullName: "Local SEO Services",
    icon: MapPin,
    color: "blue",
    colorClass: "text-blue-400",
    bgClass: "bg-blue-500/10 border-blue-500/20",
    desc: (city) => `Dominate the Google Map Pack and local search results in ${city}. We optimize your Google Business Profile, build local citations, and drive foot traffic from nearby customers.`,
    benefits: (city) => [
      `Google Business Profile optimization for ${city}`,
      `${city}-area citation building (50+ directories)`,
      "Review generation & reputation management",
      "Local schema markup implementation",
      "Map pack tracking & competitor monitoring",
      "AI-powered local search visibility",
    ],
    faqs: (city) => [
      { q: `How do I rank in the Google Map Pack in ${city}?`, a: `Ranking in ${city}'s map pack requires a verified, optimized Google Business Profile, consistent NAP (Name, Address, Phone) across local directories, positive reviews, local content, and proximity signals. We handle all of this as part of our local SEO service.` },
      { q: `How many reviews do I need to compete in ${city}?`, a: `It depends on your industry. In ${city}, most competitive industries require 30-50+ reviews with a 4.5+ average to consistently rank in the map pack. We implement review generation strategies to help you build reviews naturally.` },
      { q: "Do I need a physical office in the city?", a: "For Google Map Pack visibility, you need either a physical location or a verified service area that includes the city. We can advise on the best setup for your business type." },
    ],
  },
  "gbp-optimization": {
    name: "GBP Optimization",
    fullName: "Google Business Profile Optimization",
    icon: Globe,
    color: "yellow",
    colorClass: "text-yellow-400",
    bgClass: "bg-yellow-500/10 border-yellow-500/20",
    desc: (city) => `Maximize your Google Business Profile's visibility in ${city}. From photos and posts to Q&A and review management — we make your GBP a lead-generating machine.`,
    benefits: (city) => [
      `Full GBP audit & optimization for ${city}`,
      "Weekly GBP posts with local content",
      "Photo & video optimization",
      "Q&A management & FAQ seeding",
      "Review response & generation strategy",
      "GBP insights tracking & monthly reports",
    ],
    faqs: (city) => [
      { q: "How often should I post on Google Business Profile?", a: "We recommend 2-3 posts per week for maximum visibility. Posts expire after 7 days in search, so consistent posting keeps your profile fresh and signals to Google that your business is active." },
      { q: `What categories should my ${city} business use?`, a: `Category selection is critical for GBP visibility. We research the most effective primary and secondary categories for your industry in ${city}, analyzing what top-ranking competitors use and what drives the most map pack appearances.` },
      { q: "Can GBP optimization really drive more calls?", a: "Absolutely. An optimized GBP typically sees 2-3x more calls, direction requests, and website clicks than an unoptimized profile. For local businesses, GBP is often the highest-ROI marketing channel." },
    ],
  },
};

const CityServicePage = ({ citySlug, serviceSlug }) => {
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false);
  const city = CITY_DATA[citySlug];
  const service = SERVICE_DATA[serviceSlug];

  if (!city || !service) return <div className="min-h-screen pt-32 text-center text-white">Page not found</div>;

  const title = `${service.fullName} in ${city.name}, TX | Digital Media Ninja`;
  const description = `${service.fullName} for ${city.name}, Texas businesses. ${service.desc(city.name).slice(0, 100)}...`;
  const canonical = `https://digitalmedianinja.com/${serviceSlug}-${citySlug}/`;
  const ServiceIcon = service.icon;

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid={`city-service-page-${serviceSlug}-${citySlug}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Hero */}
      <section className="container-custom max-w-6xl mx-auto px-4 mb-16">
        <div className="text-center">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${service.bgClass} ${service.colorClass} text-sm font-medium mb-6`}>
            <MapPin className="w-4 h-4" /> {city.name}, Texas
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {service.fullName} in<br />
            <span className={service.colorClass}>{city.name}, TX</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-8">{service.desc(city.name)}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/free-ai-visibility-audit">
              <Button className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold px-8 py-3" data-testid="city-cta-audit">
                Free AI Visibility Audit <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button variant="outline" className="border-white/20 text-gray-300 hover:bg-white/5 px-8 py-3" onClick={() => setQuestionnaireOpen(true)} data-testid="city-cta-quote">
              Get a Quote for {city.name}
            </Button>
          </div>
        </div>
      </section>

      {/* Why City */}
      <section className="container-custom max-w-5xl mx-auto px-4 mb-16">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
            <MapPin className="w-6 h-6 text-[#2ABFBF] mx-auto mb-3" />
            <div className="text-2xl font-bold text-white mb-1">{city.pop}</div>
            <div className="text-sm text-gray-400">{city.name} metro population</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
            <TrendingUp className="w-6 h-6 text-[#2ABFBF] mx-auto mb-3" />
            <div className="text-2xl font-bold text-white mb-1">{city.region}</div>
            <div className="text-sm text-gray-400">Growing Texas market</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
            <Star className="w-6 h-6 text-[#2ABFBF] mx-auto mb-3" />
            <div className="text-2xl font-bold text-white mb-1">5.0</div>
            <div className="text-sm text-gray-400">Google client rating</div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="container-custom max-w-4xl mx-auto px-4 mb-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10">What's Included</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {service.benefits(city.name).map((benefit, i) => (
            <div key={i} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`city-benefit-${i}`}>
              <CheckCircle2 className="w-5 h-5 text-[#2ABFBF] shrink-0 mt-0.5" />
              <span className="text-gray-300 text-sm">{benefit}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Related Services */}
      <section className="container-custom max-w-5xl mx-auto px-4 mb-16">
        <h2 className="text-2xl font-bold text-white text-center mb-8">Other Services in {city.name}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {Object.entries(SERVICE_DATA).filter(([slug]) => slug !== serviceSlug).map(([slug, svc]) => {
            const SvcIcon = svc.icon;
            return (
              <Link key={slug} to={`/${slug}-${citySlug}`} className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors" data-testid={`city-related-${slug}`}>
                <SvcIcon className={`w-6 h-6 ${svc.colorClass} mb-3`} />
                <h3 className="text-white font-semibold mb-1">{svc.fullName}</h3>
                <p className="text-gray-400 text-xs">{city.name}, TX</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Also Explore Other Cities */}
      <section className="container-custom max-w-5xl mx-auto px-4 mb-16">
        <h2 className="text-2xl font-bold text-white text-center mb-8">{service.fullName} in Other Texas Cities</h2>
        <div className="flex flex-wrap justify-center gap-3">
          {Object.entries(CITY_DATA).filter(([slug]) => slug !== citySlug).map(([slug, c]) => (
            <Link key={slug} to={`/${serviceSlug}-${slug}`} className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-gray-300 text-sm hover:bg-white/10 transition-colors" data-testid={`city-other-${slug}`}>
              {c.name}, TX
            </Link>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="container-custom max-w-3xl mx-auto px-4 mb-16">
        <h2 className="text-2xl font-bold text-white text-center mb-10">{service.fullName} in {city.name} — FAQs</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {service.faqs(city.name).map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white/5 border border-white/10 rounded-xl px-5">
              <AccordionTrigger className="text-white text-left text-sm font-medium hover:no-underline">{faq.q}</AccordionTrigger>
              <AccordionContent className="text-gray-400 text-sm">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Bottom CTA */}
      <section className="container-custom max-w-4xl mx-auto px-4 text-center">
        <div className="bg-[#111] border border-white/10 rounded-2xl p-10">
          <h2 className="text-2xl font-bold text-white mb-3">Ready to Grow in {city.name}?</h2>
          <p className="text-gray-400 mb-6">Get a free AI Visibility Audit for your {city.name} business. We'll show you exactly where you stand against local competitors.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/free-ai-visibility-audit">
              <Button className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold px-8" data-testid="city-bottom-cta">Free AI Visibility Audit</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white/20 text-gray-300">Contact Us</Button>
            </Link>
          </div>
        </div>
      </section>

      <ServiceQuestionnaireModal isOpen={questionnaireOpen} onClose={() => setQuestionnaireOpen(false)} serviceName={`${service.fullName} — ${city.name}, TX`} />
    </div>
  );
};

export { CITY_DATA, SERVICE_DATA };
export default CityServicePage;
